/**
 * LES MEMBRES DE L'ENTREPRISE ET L'ÉTAT DE LEUR NUMÉRO (LOT F4).
 * ============================================================================
 *
 * INTENTION : sur la fiche d'une surveillance, le client lit qui est prévenu
 * et si le numéro de chacun permet de recevoir un SMS. Un employé ne voit
 * jamais un numéro.
 *
 * La route 9 (`GET /alarmes-v2/membre`) sert, pour chaque membre, son nom, son
 * rôle, `phone_present` et `phone_a_corriger` ; au propriétaire et à
 * l'administrateur seuls, elle ajoute `phone` et `phone_masque`. Ce store ne
 * garde JAMAIS `phone` : l'écran ne montre que le masque, et seulement quand
 * la matrice ouvre `voir_numero_membre` (`QuiEstPrevenu.vue`).
 *
 * Un état de numéro qui n'est pas servi en booléen reste inconnu : il ne se
 * lit jamais « sans numéro ».
 *
 * Tenu par session (personne et entreprise) : une fin de lecture qui n'est plus
 * la courante est jetée sans toucher à l'état. Chaque visite d'une fiche relit
 * (`relire`) : l'état d'un numéro corrigé ailleurs n'est jamais servi périmé.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type { AlarmesV2Membre } from "@modules/alarmes-v2/types/alarmes-v2"

/** Où en est la lecture des membres. */
export type StatutMembres = "attente" | "chargement" | "pret" | "erreur"

const chaineOuNull = (v: unknown): string | null =>
    typeof v === "string" && v !== "" ? v : null

/**
 * Un membre servi, réduit à ce que l'écran lit : jamais `phone`.
 * @param brut - une ligne de `items`
 */
export function lireLeMembre(brut: unknown): AlarmesV2Membre | null {
    if (brut === null || typeof brut !== "object") return null
    const m = brut as Record<string, unknown>
    if (typeof m._id !== "string" || m._id === "") return null
    const lu: AlarmesV2Membre = {
        _id: m._id,
        prenom: chaineOuNull(m.prenom),
        nom: chaineOuNull(m.nom),
        role: chaineOuNull(m.role),
    }
    if (typeof m.phone_present === "boolean") lu.phone_present = m.phone_present
    if (typeof m.phone_a_corriger === "boolean")
        lu.phone_a_corriger = m.phone_a_corriger
    if ("phone_masque" in m) lu.phone_masque = chaineOuNull(m.phone_masque)
    return lu
}

export const useMembresStore = defineStore("alarmesV2Membres", () => {
    /** Les membres lus, par identifiant. */
    const parId = ref<Record<string, AlarmesV2Membre>>({})

    const statut = ref<StatutMembres>("attente")
    const erreur = ref<ErreurAlarmesV2 | null>(null)

    /** La clé de session de la lecture faite ou en cours. */
    const cleLue = ref("")

    let lectureCourante = 0

    /**
     * Lit les membres de l'entreprise courante. Ne rejette jamais.
     *
     * Sans `relire`, une lecture faite pour la même session sert telle quelle ;
     * avec `relire` (chaque visite d'une fiche), l'état des numéros est relu,
     * pour qu'un numéro corrigé ailleurs ne reste jamais « à corriger ».
     * @param options - `relire` : relire même si la session a déjà été lue
     */
    async function charger(options: { relire?: boolean } = {}): Promise<void> {
        const cle = cleDeSession()
        if (!cle) {
            reinitialiser()
            return
        }
        if (
            !options.relire &&
            cleLue.value === cle &&
            statut.value !== "erreur"
        )
            return

        const numero = ++lectureCourante
        cleLue.value = cle
        parId.value = {}
        erreur.value = null
        statut.value = "chargement"
        const estCourante = () =>
            numero === lectureCourante && cleDeSession() === cle
        try {
            const reponse = await serviceAlarmesV2.alarmesV2ListerMembres()
            if (!estCourante()) return
            if (!reponse || !Array.isArray(reponse.items)) {
                erreur.value = new ErreurAlarmesV2({
                    code: CODE_INDISPONIBLE,
                    statut: null,
                })
                statut.value = "erreur"
                return
            }
            const lus: Record<string, AlarmesV2Membre> = {}
            for (const brut of reponse.items) {
                const m = lireLeMembre(brut)
                if (m) lus[m._id] = m
            }
            parId.value = lus
            statut.value = "pret"
        } catch (e) {
            if (!estCourante()) return
            erreur.value =
                e instanceof ErreurAlarmesV2 ? e : versErreurAlarmesV2(e)
            statut.value = "erreur"
        }
    }

    /**
     * Relit les membres après une écriture (un numéro corrigé), SANS vider la
     * liste lue : les noms et l'état des numéros restent montrés pendant la
     * relecture, puis sont remplacés d'un coup quand elle aboutit. Un échec
     * ne touche à rien et rend faux : l'écran dit « non relu ». Sans liste
     * lue, c'est une lecture ordinaire. Ne rejette jamais.
     */
    async function relireApresEcriture(): Promise<boolean> {
        const cle = cleDeSession()
        if (!cle) return false
        if (statut.value !== "pret" || cleLue.value !== cle) {
            await charger({ relire: true })
            return statut.value === "pret" && cleLue.value === cle
        }
        const numero = ++lectureCourante
        const estCourante = () =>
            numero === lectureCourante && cleDeSession() === cle
        try {
            const reponse = await serviceAlarmesV2.alarmesV2ListerMembres()
            if (!estCourante()) return false
            if (!reponse || !Array.isArray(reponse.items)) return false
            const lus: Record<string, AlarmesV2Membre> = {}
            for (const brut of reponse.items) {
                const m = lireLeMembre(brut)
                if (m) lus[m._id] = m
            }
            parId.value = lus
            return true
        } catch {
            return false
        }
    }

    /**
     * Un membre, `null` s'il n'est pas dans la liste lue.
     * @param id - l'identifiant de la personne
     */
    function membre(id: string | null | undefined): AlarmesV2Membre | null {
        return id ? (parId.value[id] ?? null) : null
    }

    /** Remet le store à son état de départ ; toute lecture en vol est jetée. */
    function reinitialiser(): void {
        lectureCourante += 1
        parId.value = {}
        erreur.value = null
        statut.value = "attente"
        cleLue.value = ""
    }

    return {
        parId,
        statut,
        erreur,
        cleLue,
        charger,
        relireApresEcriture,
        membre,
        reinitialiser,
    }
})
